/**
 * Header menu controller.
 *
 * Ported from the design prototype (1.homepage.html), which handles the basics
 * -- toggle, Escape, close-on-link-click -- and is missing four things a real
 * site needs:
 *
 *  1. No focus trap. Tabbing out of an open full-screen overlay lands on the
 *     page behind it -- which is NOT inert, so focus leaves the modal for
 *     content the overlay is covering and nothing on screen shows where it
 *     went. (This used to read "which is invisible to the user." Since A2 that
 *     is no longer true of the whole page: `.rf-nav` is raised above the open
 *     overlay, so the wordmark and the burger are both visible and pointer-
 *     reachable while the menu is open. BOTH are therefore in the trap's ring --
 *     see `trapRing` below.
 *
 *     This sentence contradicted itself for the length of one commit, and the
 *     contradiction is worth leaving a scar for: it read "the wordmark
 *     deliberately is NOT [in the trap] ... stays a POINTER-ONLY affordance"
 *     with "and it is IN the ring" appended to the same sentence. I wrote the
 *     appended clause in U14 without re-reading the clause it was joined to. The
 *     wordmark IS in the ring. It is navigation rather than dismissal, which is
 *     why it is not the *close* control, but that is not a reason to keep it off
 *     the keyboard.
 *
 *     That was a real gap until 31 Aug 2026, and the comment here used to paper
 *     over it: it claimed "a keyboard user reaches `/` through the menu's own
 *     links", which is false. No nav_link points at `/` -- they go to
 *     /pages/how-it-works, /pages/pricing, /pages/the-guarantee,
 *     /pages/photo-books and /pages/help -- and the foot adds only an external
 *     account URL and /pages/build-your-order. The one `url: '/'` in
 *     header-group.json belongs to the brand bar's mark, a different section
 *     outside the overlay. Kurtis asked for a wordmark-to-home and an X-to-close;
 *     both already existed and were hidden under the overlay, so A2 restored them
 *     for pointers and trapRing() now extends the wordmark half to the keyboard.)
 *  2. Focus is never returned to the burger on close, so keyboard users are
 *     dropped at the top of the document.
 *  3. Resizing past the desktop breakpoint while open leaves body scroll locked
 *     with no visible menu and no way to unlock it.
 *  4. It binds once at load, so it breaks when the theme editor re-renders the
 *     header section.
 *
 * The overlay is marked inert while closed, which removes it from both the tab
 * order and the accessibility tree.
 *
 * WHERE EACH LISTENER LIVES, and why it is not a style choice (U5).
 *
 *   ELEMENT-LEVEL (burger click, menu click) -> registered in setup(), once per
 *   pair, guarded by the `rfBound` flag. A re-rendered section brings new
 *   elements that carry no handlers, so they have to be rebound; the old ones
 *   are detached and take their handlers with them.
 *
 *   DOCUMENT- AND WINDOW-LEVEL (keydown, the matchMedia change) -> registered
 *   ONCE at module level. Registering these in setup() looked symmetrical and
 *   was the bug: the theme editor's second `shopify:section:load` bound a
 *   SECOND document keydown handler while the first stayed registered, closed
 *   over the DETACHED pair. On Escape the older handler ran first, read the
 *   shared `document.body` class as open, and "closed" the dead pair -- clearing
 *   the class. The live handler then saw a closed menu and returned, so the live
 *   overlay was never made inert, the live burger kept aria-expanded="true", and
 *   focus was returned zero times. Both handlers resolve the LIVE pair with
 *   `document.querySelector` at event time: a detached root cannot be reached by
 *   a document query, so there is exactly one candidate and nothing to store,
 *   stale-check or clear.
 *
 *   tests/listener-placement.test.js pins the placement statically;
 *   docs/interaction-test.sh drives two section loads and one Escape.
 */
(function () {
  'use strict';

  var OPEN_CLASS = 'pk-menu-open';
  var DESKTOP_QUERY = '(min-width: 961px)';

  var FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  /* ── the live pair ───────────────────────────────────────────────────────
     isOpen/focusables/setOpen take the pair as arguments rather than closing
     over one, so the module-level handlers can pass whichever pair is in the
     document when the event fires. Behaviour for a single render is unchanged:
     the same two elements, in the same order, doing the same things. */

  function isOpen() {
    return document.body.classList.contains(OPEN_CLASS);
  }

  /* Interactably rendered: it has a box, or it is already the focused element.
     ONE definition, used by focusables() and trapRing() both -- they had a
     copy each, with a comment on the second saying "same test as focusables()",
     which is the kind of claim that stays true only until someone tightens one
     of them (ce:review, 31 Aug 2026). */
  function isRendered(el) {
    return !!el && (el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement);
  }

  function focusables(menu) {
    return Array.prototype.filter.call(menu.querySelectorAll(FOCUSABLE), isRendered);
  }

  function setOpen(burger, menu, open) {
    document.body.classList.toggle(OPEN_CLASS, open);
    burger.setAttribute('aria-expanded', String(open));
    burger.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');

    if (open) {
      menu.removeAttribute('inert');
      var first = focusables(menu)[0];
      if (first) first.focus();
    } else {
      // Read this BEFORE setting inert. Making an ancestor inert evicts focus
      // from its descendants, so checking afterwards can see document.body and
      // skip restoring focus even though the user really was inside the menu.
      var hadFocusInside = menu.contains(document.activeElement);
      menu.setAttribute('inert', '');
      if (hadFocusInside) burger.focus();
    }
  }

  /* ── the trap's tab ring ─────────────────────────────────────────────────
     THE BURGER IS PART OF THE RING, and it is not inside the overlay, so
     `focusables()` -- scoped to the menu element -- cannot reach it. It is added
     here explicitly rather than by widening that scope.

     WHY IT HAS TO BE. A2 raised `.rf-nav` above the open overlay, which turned
     the burger into a control the user can SEE. `.pk-menu` has no close button
     and nothing binds a backdrop click, so the burger is the overlay's only
     pointer-driven dismiss -- and a visible dismiss control a keyboard or switch
     user cannot operate would leave Escape as the only exit with nothing on
     screen advertising it. Before A2 the burger sat UNDERNEATH the overlay, so
     the same unreachability was invisible and this was not yet a defect.

     PREPENDED, NOT APPENDED, and the order is load-bearing. The burger precedes
     `.pk-menu` in the DOM, so prepending makes the ring match native tab order:
     burger -> first menu link -> ... -> foot CTA -> (wrap) burger. Appending it
     instead would make the FOOT CTA a non-terminal item, and a Tab from the foot
     CTA would then fall straight through to the page behind the overlay -- the
     exact escape reason 1 above exists to prevent. Shift+Tab from the first menu
     item lands on the burger either way; in DOM order it needs no
     preventDefault to do it.

     `focusables(menu)[0]` IS UNTOUCHED, deliberately: `setOpen` focuses it on
     open, and the existing overlay assertions are written against it.

     THE WORDMARK IS IN THE RING TOO, added 31 Aug 2026 on Kurtis's instruction
     after he reported an open menu with "no exit or home option". Both controls
     turned out to exist already -- the burger morphs to an X via
     `[aria-expanded='true']` and the wordmark links to `/` -- and both were simply
     PAINTED UNDER the opaque overlay, which is the defect A2 fixed. So a mouse
     user now has both. A keyboard user had only the X, because the wordmark sits
     in `.rf-nav` and `focusables()` is scoped to the menu; that asymmetry is what
     this closes.

     Ring order follows DOM order inside `.rf-nav` -- wordmark, then burger --
     so native Tab traversal and the wrap agree: wordmark -> burger -> first menu
     link -> ... -> foot CTA -> (wrap) wordmark. Appending either instead would
     make the FOOT CTA a non-terminal item, and a Tab from it would fall straight
     through to the page behind the overlay -- the exact escape reason 1 above
     exists to prevent.

     `focusables(menu)[0]` IS UNTOUCHED, deliberately: `setOpen` focuses it on
     open, and the existing overlay assertions are written against it.

     At >=961px the burger is `display: none`, so a ring whose endpoint cannot
     take focus would swallow the wrap instead of performing it -- hence
     isRendered() on each. (The matchMedia handler below closes the menu on that
     crossing, so this is belt and braces, not the live path.) */
  function trapRing(pair) {
    var items = focusables(pair.menu);
    if (isRendered(pair.burger)) items.unshift(pair.burger);
    var nav = pair.burger.closest ? pair.burger.closest('.rf-nav') : null;
    var logo = (nav || document).querySelector('.rf-logo');
    if (isRendered(logo)) items.unshift(logo);
    return items;
  }

  /** The pair currently in the document. A detached root cannot be found here. */
  function livePair() {
    var burger = document.querySelector('[data-rf-burger]');
    var menu = document.querySelector('[data-rf-menu]');
    return burger && menu ? { burger: burger, menu: menu } : null;
  }

  /* ── per-render setup ───────────────────────────────────────────────────
     The initial state and the two ELEMENT-level clicks only. Nothing here
     touches document or window -- see the note at the top of the file. */

  function setup(root) {
    var scope = root && root.querySelector ? root : document;
    var burger = scope.querySelector('[data-rf-burger]');
    var menu = scope.querySelector('[data-rf-menu]');

    if (!burger || !menu || burger.dataset.rfBound === 'true') return;
    burger.dataset.rfBound = 'true';

    menu.setAttribute('inert', '');
    burger.setAttribute('aria-expanded', 'false');

    burger.addEventListener('click', function () {
      setOpen(burger, menu, !isOpen());
    });

    menu.addEventListener('click', function (event) {
      if (event.target.closest('a')) setOpen(burger, menu, false);
    });
  }

  function init() {
    setup(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* ── registered once, for the life of the document ─────────────────────── */

  document.addEventListener('keydown', function (event) {
    if (!isOpen()) return;
    var pair = livePair();
    if (!pair) return;

    if (event.key === 'Escape') {
      setOpen(pair.burger, pair.menu, false);
      return;
    }

    if (event.key !== 'Tab') return;

    var items = trapRing(pair);
    if (!items.length) return;

    var first = items[0];
    var last = items[items.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  // Crossing into desktop hides the overlay in CSS. Without this the body
  // stays scroll-locked with nothing on screen to close.
  //
  // ONE MediaQueryList, created once. A second object per re-render would carry
  // its own listener list, and every one of them would fire on every crossing.
  var desktop = window.matchMedia ? window.matchMedia(DESKTOP_QUERY) : null;
  if (desktop) {
    var onBreakpoint = function (event) {
      if (!event.matches || !isOpen()) return;
      var pair = livePair();
      if (pair) setOpen(pair.burger, pair.menu, false);
    };
    if (typeof desktop.addEventListener === 'function') {
      desktop.addEventListener('change', onBreakpoint);
    } else if (typeof desktop.addListener === 'function') {
      desktop.addListener(onBreakpoint);
    }
  }

  // The theme editor swaps the header's DOM without a reload.
  document.addEventListener('shopify:section:load', function (event) {
    document.body.classList.remove(OPEN_CLASS);
    setup(event.target);
  });

  document.addEventListener('shopify:section:unload', function () {
    document.body.classList.remove(OPEN_CLASS);
  });
})();
